import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Lock, MailCheck, ShieldCheck } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { formatDate } from '@/shared/utils/format';
import {
  apiErrorCode,
  apiErrorMessage,
  getReferralViewerToken,
  referralsApi,
  setReferralViewerToken,
  type ReferralGateInfo,
} from '../api/referralsApi';
import { referralKeys } from '../queryKeys';
import { AttachmentDropzone, RecipientActions, ReferralSlipView, ReferralTimeline, STATUS_HEADLINE } from '../components';
import styles from './publicPage.module.css';

/**
 * /refer/v/:token — the receiving office opens the slip from its email, text or printed QR code.
 *
 * The link is unguessable, but the address it went to is unverified, so the slip sits behind a
 * gate: enter the email the referral was sent to, type the 6-digit code we mail there, and this
 * browser earns a 30-day viewer token. No password, no sign-up form. The first time an office does
 * this it becomes a verified contact for PracticeNova; after they act on the slip, one optional
 * nudge asks them to confirm their practice details.
 */
export function PublicReferralPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [hasViewer, setHasViewer] = useState<boolean>(() => Boolean(getReferralViewerToken()));
  const [justVerified, setJustVerified] = useState(false);

  const gate = useQuery({
    queryKey: [...referralKeys.publicView(token), 'gate'] as const,
    queryFn: () => referralsApi.publicGate(token),
    enabled: Boolean(token),
    retry: false,
  });
  const view = useQuery({
    queryKey: referralKeys.publicView(token),
    queryFn: () => referralsApi.publicView(token),
    enabled: Boolean(token) && hasViewer && gate.isSuccess,
    retry: false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: referralKeys.publicView(token) });

  // A stale or foreign viewer token: back to the gate.
  useEffect(() => {
    if (view.isError && apiErrorCode(view.error) === 'CODE_REQUIRED') {
      setReferralViewerToken(null);
      setHasViewer(false);
    }
  }, [view.isError, view.error]);

  if (gate.isLoading || (hasViewer && view.isLoading)) {
    return (
      <div className={styles.page}>
        <div className={styles.center}>
          <Spinner />
        </div>
      </div>
    );
  }
  if (gate.isError || !gate.data) {
    return (
      <div className={styles.page}>
        <div className={styles.center}>
          <h1 className={styles.title}>Referral unavailable</h1>
          <p className={styles.lead}>{apiErrorMessage(gate.error, 'This referral link is not valid.')}</p>
        </div>
      </div>
    );
  }

  if (!hasViewer || !view.data) {
    if (view.isError && apiErrorCode(view.error) === 'WRONG_OFFICE') {
      return (
        <div className={styles.page}>
          <div className={styles.center}>
            <h1 className={styles.title}>This referral is for a different office</h1>
            <p className={styles.lead}>
              {apiErrorMessage(view.error, '')} If you work at {gate.data.officeName}, verify that office's email instead.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                setReferralViewerToken(null);
                setHasViewer(false);
              }}
            >
              Verify a different email
            </Button>
          </div>
        </div>
      );
    }
    return (
      <Gate
        token={token}
        info={gate.data}
        prefillEmail={params.get('e') ?? ''}
        onVerified={(viewerToken, firstTime) => {
          setReferralViewerToken(viewerToken);
          setJustVerified(firstTime);
          setHasViewer(true);
        }}
      />
    );
  }

  const r = view.data.referral;
  const superseded = view.data.superseded;
  const headline = superseded ? 'The patient booked with another office' : STATUS_HEADLINE[r.status].received;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.brand}>
          <span>
            Referral via <span className={styles.brandName}>PracticeNova</span>
          </span>
          {view.data.recipientOnPracticeNova ? (
            <Link to={`/referrals/${r.id}`} className={styles.ctaSecondary}>
              Open in your PracticeNova inbox
            </Link>
          ) : (
            <span className={styles.note}>
              <ShieldCheck size={12} /> Verified for {r.recipient?.name ?? 'the receiving office'}
            </span>
          )}
        </div>

        {justVerified && (
          <div className={styles.infoCard}>
            <MailCheck size={18} />
            <div>
              <strong>You're in.</strong> This browser stays verified for 30 days, so the next referral from any office opens without a
              code. Nothing else to set up.
            </div>
          </div>
        )}

        <div className={styles.card}>
          <div className={styles.banner}>
            <div>
              <div className={styles.bannerTitle}>{headline}</div>
              <div className={styles.bannerSub}>
                From {r.referringDoctorName ? `${r.referringDoctorName}, ` : ''}
                {r.senderPracticeName}
                {r.sentAt ? ` · ${formatDate(r.sentAt, 'MMM d, yyyy h:mm a')}` : ''}
                {r.scheduledFor ? ` · Appointment ${formatDate(r.scheduledFor, 'EEE, MMM d h:mm a')}` : ''}
              </div>
            </div>
            {!superseded && (
            <RecipientActions
              status={r.status}
              askActorName
              onStatus={(input) => referralsApi.publicStatus(token, input).then(refresh).catch((e) => toast.error(apiErrorMessage(e, 'Could not update the referral')))}
              onReport={(input) => referralsApi.publicReport(token, input).then(refresh).then(() => toast.success('Report sent to the referring office.')).catch((e) => toast.error(apiErrorMessage(e, 'Could not send the report')))}
              onNote={(note, actorName) => referralsApi.publicNote(token, note, actorName).then(refresh).catch((e) => toast.error(apiErrorMessage(e, 'Could not add the note')))}
            />
            )}
          </div>
          {superseded && (
            <p className={styles.note}>
              {r.senderPracticeName} offered this patient more than one office, and another one confirmed the booking. No action is needed; this
              copy stays for your records.
            </p>
          )}

          <ReferralSlipView
            referral={r}
            viewer="recipient"
            attachmentsSlot={
              <AttachmentDropzone
                attachments={r.attachments}
                requestUpload={r.status !== 'cancelled' ? (input) => referralsApi.publicRequestUpload(token, input) : undefined}
                confirmUpload={r.status !== 'cancelled' ? (aid) => referralsApi.publicConfirmUpload(token, aid) : undefined}
                onChanged={() => void refresh()}
                disabledReason={r.attachments.length === 0 ? 'No files attached.' : null}
                compact
              />
            }
          />

          <h4 className={styles.sectionTitle}>Activity</h4>
          <ReferralTimeline events={r.events} />
        </div>

        {!view.data.recipientOnPracticeNova && (r.status === 'scheduled' || r.status === 'completed' || r.status === 'declined' || r.reports.length > 0) && (
          <LeadNudge token={token} officeName={r.recipient?.name ?? ''} officePhone={r.recipient?.phone ?? ''} />
        )}

        <p className={styles.footer}>
          This page is private to your office. Receive referrals in one place, with X-rays and reports attached?{' '}
          <a href="https://www.practicenova.ai" target="_blank" rel="noreferrer">
            Learn about PracticeNova
          </a>
          .
        </p>
      </div>
    </div>
  );
}

// ─── The gate ────────────────────────────────────────────────────────────────

function Gate({
  token,
  info,
  prefillEmail,
  onVerified,
}: {
  token: string;
  info: ReferralGateInfo;
  prefillEmail: string;
  onVerified: (viewerToken: string, firstTime: boolean) => void;
}) {
  const [email, setEmail] = useState(prefillEmail);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const requestCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await referralsApi.publicRequestCode(token, email);
      setSentTo(r.sentTo);
      setStep('code');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send a code.'));
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await referralsApi.publicVerifyCode(token, email, code);
      onVerified(r.viewerToken, r.firstTime);
    } catch (err) {
      setError(apiErrorMessage(err, 'That code did not work.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={`${styles.container} ${styles.narrow}`}>
        <div className={styles.brand}>
          <span>
            Referral via <span className={styles.brandName}>PracticeNova</span>
          </span>
        </div>
        <div className={styles.card}>
          <div className={styles.gateHead}>
            <Lock size={20} />
            <div>
              <h1 className={styles.title}>A patient referral for {info.officeName}</h1>
              <p className={styles.lead}>
                Sent by <strong>{info.senderPracticeName}</strong>. To protect the patient's details, confirm you can read the email
                address this referral was sent to. No account or password needed.
              </p>
            </div>
          </div>

          {step === 'email' ? (
            <form
              className={styles.gateForm}
              onSubmit={(e) => {
                e.preventDefault();
                void requestCode();
              }}
            >
              <Input
                label={info.emailOnFile ? `Email address${info.emailHint ? ` (the one on file is ${info.emailHint})` : ''}` : 'Your office email address'}
                type="email"
                fullWidth
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="frontdesk@yourpractice.com"
                error={error ?? undefined}
                hint={info.emailOnFile ? 'It must match the address the referral was sent to.' : 'The referral reached you by text or on paper, so any office address works. We will send a 6-digit code there.'}
              />
              <Button type="submit" loading={busy} disabled={!email.trim()}>
                Email me a code
              </Button>
              {info.onPracticeNova && (
                <p className={styles.note}>
                  Already use PracticeNova? <Link to={`/referrals/${info.referralId}`}>Sign in to open it in your inbox</Link> instead.
                </p>
              )}
            </form>
          ) : (
            <form
              className={styles.gateForm}
              onSubmit={(e) => {
                e.preventDefault();
                void verify();
              }}
            >
              <p className={styles.lead}>
                We sent a 6-digit code to <strong>{sentTo ?? email}</strong>. It works for 15 minutes.
              </p>
              <Input
                label="Code"
                inputMode="numeric"
                autoComplete="one-time-code"
                fullWidth
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                error={error ?? undefined}
              />
              <div className={styles.ctaRow}>
                <Button type="submit" loading={busy} disabled={code.length !== 6}>
                  Open the referral
                </Button>
                <Button type="button" variant="ghost" onClick={() => void requestCode()} disabled={busy}>
                  Send a new code
                </Button>
                <Button type="button" variant="ghost" onClick={() => setStep('email')} disabled={busy}>
                  Use a different email
                </Button>
              </div>
            </form>
          )}
        </div>
        <p className={styles.footer}>
          Why a code? The link alone could be forwarded. The code proves the person opening it belongs to the office the referral
          was addressed to.
        </p>
      </div>
    </div>
  );
}

// ─── Lead nudge ──────────────────────────────────────────────────────────────

function LeadNudge({ token, officeName, officePhone }: { token: string; officeName: string; officePhone: string }) {
  const toast = useToast();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(`pn_referral_nudge_${token}`) === '1';
    } catch {
      return false;
    }
  });
  const [form, setForm] = useState({ contactName: '', practiceName: officeName, phone: officePhone });
  const [busy, setBusy] = useState(false);
  const done = () => {
    setDismissed(true);
    try {
      localStorage.setItem(`pn_referral_nudge_${token}`, '1');
    } catch {
      /* ignore */
    }
  };
  if (dismissed) return null;
  return (
    <div className={styles.card}>
      <h4 className={styles.sectionTitle}>Want every referral in one inbox?</h4>
      <p className={styles.lead}>
        Offices that refer to you through PracticeNova can reach you here, with X-rays and reports attached. Confirm two details and
        we'll keep this office's referrals together. Optional.
      </p>
      <div className={styles.gateForm}>
        <Input label="Your name" fullWidth value={form.contactName} onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))} placeholder="Dr. A Rega / Front desk" />
        <Input label="Practice name" fullWidth value={form.practiceName} onChange={(e) => setForm((f) => ({ ...f, practiceName: e.target.value }))} />
        <Input label="Office phone" fullWidth value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
        <div className={styles.ctaRow}>
          <Button
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await referralsApi.publicClaim(token, form);
                toast.success('Thanks — saved.');
                done();
              } catch (err) {
                toast.error(apiErrorMessage(err, 'Could not save'));
              } finally {
                setBusy(false);
              }
            }}
          >
            Save
          </Button>
          <Button variant="ghost" onClick={done}>
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
